import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

export const submissionService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('employee_submissions')?.select(`
          *,
          company:companies(name, type, logo_url),
          user:user_profiles!user_id(full_name, email)
        `)?.order('submitted_at', { ascending: false });

      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }

      // Return empty array if no data
      if (!data || data.length === 0) {
        return [];
      }

      // Convert snake_case to camelCase
      return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      companyId: row?.company_id,
      fullName: row?.full_name,
      email: row?.email,
      phoneNumber: row?.phone_number,
      dateOfBirth: row?.date_of_birth,
      currentAddress: row?.current_address,
      stateOfOrigin: row?.state_of_origin,
      lga: row?.lga,
      callUpNumber: row?.call_up_number,
      stateOfDeployment: row?.state_of_deployment,
      ppaName: row?.ppa_name,
      batchNumber: row?.batch_number,
      institutionName: row?.institution_name,
      degreeProgram: row?.degree_program,
      graduationYear: row?.graduation_year,
      emergencyContactName: row?.emergency_contact_name,
      emergencyContactRelationship: row?.emergency_contact_relationship,
      emergencyContactPhone: row?.emergency_contact_phone,
      profilePhotoUrl: row?.profile_photo_url,
      callUpLetterUrl: row?.call_up_letter_url,
      degreeCertificateUrl: row?.degree_certificate_url,
      submissionStatus: row?.submission_status,
      submittedAt: row?.submitted_at,
      reviewedAt: row?.reviewed_at,
      reviewedBy: row?.reviewed_by,
      company: row?.company,
      user: row?.user,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
    })) || [];
    } catch (err) {
      console.error('Error in getAll submissions:', err);
      throw err;
    }
  },

  /**
   * Check for existing submission to prevent duplicates
   * @param {Object} params - Parameters for duplicate check
   * @returns {Promise<Object>} - Result indicating if duplicate exists
   */
  async checkDuplicateSubmission({ userId, companyId, email, callUpNumber }) {
    try {
      const { data, error } = await supabase?.rpc('check_existing_submission', {
        p_user_id: userId,
        p_company_id: companyId,
        p_email: email,
        p_call_up_number: callUpNumber
      });

      if (error) {
        console.error('Error checking duplicate submission:', error);
        throw error;
      }

      // Return the first result (there should only be one)
      return data?.[0] || { has_existing: false };
    } catch (err) {
      console.error('Duplicate check error:', err);
      // Don't block submission on check failure, but log it
      return { has_existing: false };
    }
  },

  /**
   * Validate phone numbers to prevent fraud
   * @param {Object} formData - Form data to validate
   * @returns {Object} - Validation result with errors if any
   */
  validatePhoneNumbers(formData) {
    const errors = {};
    
    // Check if emergency contact phone matches user's phone
    if (formData?.emergencyContactPhone === formData?.phone) {
      errors.emergencyContactPhone = 'Emergency contact phone cannot be the same as your phone number';
    }
    
    // Check if emergency contact phone matches alternative phone
    if (formData?.emergencyContactPhone === formData?.alternativePhone) {
      errors.emergencyContactPhone = 'Emergency contact phone cannot be the same as your alternative phone';
    }
    
    // Check if emergency contact phone matches WhatsApp number
    if (formData?.emergencyContactPhone === formData?.whatsappNumber) {
      errors.emergencyContactPhone = 'Emergency contact phone cannot be the same as your WhatsApp number';
    }
    
    return {
      isValid: Object.keys(errors)?.length === 0,
      errors
    };
  },

  async create(submission) {
    try {
      // Validate phone numbers first (client-side check)
      const phoneValidation = this.validatePhoneNumbers(submission);
      if (!phoneValidation?.isValid) {
        throw new Error(Object.values(phoneValidation?.errors)?.[0] || 'Invalid phone number configuration');
      }

      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      // If not authenticated, create a new account using the submission email
      let userId;
      if (!user || authError) {
        // Generate a temporary password (user will need to reset it)
        const tempPassword = Math.random()?.toString(36)?.slice(-12) + 'A1!';
        
        // Create new user account
        const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
          email: submission?.email,
          password: tempPassword,
          options: {
            data: {
              full_name: `${submission?.firstName} ${submission?.lastName}`,
              role: 'employee',
            },
          },
        });

        if (signUpError) {
          // If email already exists, prompt user to sign in
          if (signUpError?.message?.includes('already registered')) {
            throw new Error('An account with this email already exists. Please sign in to continue your registration.');
          }
          throw new Error(`Account creation failed: ${signUpError?.message}`);
        }

        if (!signUpData?.user) {
          throw new Error('Failed to create user account. Please try again.');
        }

        userId = signUpData?.user?.id;

        // Note: User will need to verify their email and set a new password
        console.log('New account created for:', submission?.email);
      } else {
        userId = user?.id;
      }

      // Check for duplicate submission (server-side check)
      const duplicateCheck = await this.checkDuplicateSubmission({
        userId,
        companyId: submission?.companyId,
        email: submission?.email,
        callUpNumber: submission?.nyscCallUpNumber
      });

      if (duplicateCheck?.has_existing) {
        // Provide specific error message based on conflict type
        let errorMessage = '';
        switch (duplicateCheck?.conflict_type) {
          case 'user_company':
            errorMessage = 'You have already submitted a registration for this company. Please check your profile or contact support.';
            break;
          case 'email_company':
            errorMessage = 'This email address has already been used to register for this company.';
            break;
          case 'call_up_number':
            errorMessage = 'This NYSC call-up number has already been registered in our system.';
            break;
          default:
            errorMessage = duplicateCheck?.message || 'A registration with these details already exists.';
        }
        throw new Error(errorMessage);
      }

      // Convert camelCase to snake_case for database
      const { data, error } = await supabase?.from('employee_submissions')?.insert({
          user_id: userId,
          company_id: submission?.companyId,
          full_name: `${submission?.firstName} ${submission?.middleName ? submission?.middleName + ' ' : ''}${submission?.lastName}`,
          email: submission?.email,
          phone_number: submission?.phone,
          alternative_phone: submission?.alternativePhone || null,
          whatsapp_number: submission?.whatsappNumber || null,
          date_of_birth: submission?.dateOfBirth,
          current_address: `${submission?.streetAddress}, ${submission?.city}, ${submission?.state}${submission?.postalCode ? ', ' + submission?.postalCode : ''}`,
          state_of_origin: submission?.state,
          lga: submission?.lga,
          call_up_number: submission?.nyscCallUpNumber,
          state_of_deployment: submission?.stateOfDeployment,
          ppa_name: submission?.ppaName,
          batch_number: submission?.serviceBatch,
          institution_name: submission?.institutionName,
          degree_program: submission?.courseOfStudy,
          graduation_year: submission?.graduationYear,
          emergency_contact_name: submission?.emergencyContactName,
          emergency_contact_relationship: submission?.emergencyContactRelationship,
          emergency_contact_phone: submission?.emergencyContactPhone,
          profile_photo_url: submission?.passportPhoto,
          call_up_letter_url: submission?.nyscAssignmentLetter,
          degree_certificate_url: submission?.tertiaryGraduationCert,
        })?.select(`
          *,
          company:companies(name, type, logo_url)
        `)?.single();

      if (error) {
        console.error('Submission error:', error);
        
        // Handle specific database constraint violations
        if (error?.code === '23505') { // Unique violation
          if (error?.message?.includes('unique_user_company_submission')) {
            throw new Error('You have already submitted a registration for this company.');
          } else if (error?.message?.includes('idx_unique_email_company')) {
            throw new Error('This email has already been used to register for this company.');
          } else if (error?.message?.includes('idx_unique_call_up_number')) {
            throw new Error('This NYSC call-up number has already been registered.');
          }
        } else if (error?.code === 'P0001') { // Custom exception from trigger
          throw new Error(error?.message || 'Validation failed: Phone number conflict detected.');
        }
        
        throw new Error(`Submission failed: ${error?.message}`);
      }

      // Send registration success email
      try {
        await emailService?.sendRegistrationEmail({
          email: data?.email,
          fullName: data?.full_name,
          companyName: data?.company?.name,
          applicationId: data?.id,
        });
        console.log('Registration email sent successfully');
      } catch (emailError) {
        console.error('Failed to send registration email:', emailError);
        // Email sending is non-critical, so we continue with the registration
      }

      return data;
    } catch (err) {
      console.error('Submission service error:', err);
      throw err;
    }
  },

  async updateStatus(submissionId, status) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('employee_submissions')?.update({
        submission_status: status,
        reviewed_at: new Date()?.toISOString(),
        reviewed_by: user?.id,
      })?.eq('id', submissionId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async getUserSubmissions(userId) {
    const { data, error } = await supabase?.from('employee_submissions')?.select(`*,company:companies(name, type, logo_url)`)?.eq('user_id', userId)?.order('submitted_at', { ascending: false });

    if (error) throw error;
    return data;
  },
};